import { newGuid } from 'owa-config';
import type { QueuedActionContext, QueuedActionOptions, SubmitProcessor } from 'owa-queued-actions';
import type { StrictMutationOptions } from '../types/StrictMutationOptions';
import type { FeatureName } from 'owa-feature-flags';
import type { MutationOptions, ApolloCache } from '@apollo/client';
import { sanitize } from 'owa-client-types/lib/sanitize';

// make sure all properties of the action context are copied over.
// flight and submitProcessor are allowed to be undefined, localResult/localError/attempt
// aren't computed until after the idb resolver runs so opt those out
type ExplicitActionContext = Omit<
    Required<QueuedActionContext['queuedAction']>,
    'localResult' | 'localErr' | 'flight' | 'submitProcessor' | 'attempt' | 'waitUntil'
> & {
    flight?: FeatureName;
    submitProcessor?: SubmitProcessor;
    waitUntil?: Date;
};

export function stampQueuedActionOptionsOnContext(
    fullOptions: StrictMutationOptions<any, any, any>
): QueuedActionOptions & Omit<MutationOptions<any, any, any, ApolloCache<any>>, 'mutation'> {
    const options: QueuedActionOptions & StrictMutationOptions<any, any, any> = fullOptions || {};

    // stamp queued action information from the options into the context so they're available to links
    // (options isn't passed to links, so they need to be drilled via context)
    if (options?.queuedAction) {
        const queuedAction: ExplicitActionContext = {
            blockingKeys: options.queuedAction.blockingKeys,
            resultProcessor: options.queuedAction.resultProcessor,
            flight: options.queuedAction.flight,
            state: 'OfflineExecution',
            cachePolicy: options.fetchPolicy === 'no-cache' ? 'no-cache' : 'cache',
            uuid: newGuid(),
            submitProcessor: options.queuedAction.submitProcessor,
            mailboxInfo: sanitize(options.queuedAction.mailboxInfo),
            waitUntil: options.queuedAction.waitUntil,
        };

        const queuedContext: QueuedActionContext = {
            ...(options.context ?? {}),
            queuedAction,
        };

        options.context = queuedContext;
    }

    return options;
}
